/**
 * CardManager — Card CRUD table with search, filter, import
 */

import { useEffect, useState, useCallback } from 'react';
import { useAdminStore } from '../../../store/adminStore';
import type { CardDefinition } from '../../../services/AdminAPI';
import * as AdminAPI from '../../../services/AdminAPI';
import { CardEffectEditor } from '../CardEffectEditor';

export function CardManager() {
  const {
    cards, cardsTotalCount, cardsOffset, isLoading, error,
    loadCards, createCard, updateCard, deleteCard, importCards, importFromSheet,
  } = useAdminStore();

  const [search, setSearch] = useState('');
  const [editingCard, setEditingCard] = useState<Partial<CardDefinition> | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [sheetId, setSheetId] = useState('');
  const [showImportSheet, setShowImportSheet] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [effectEditorCard, setEffectEditorCard] = useState<CardDefinition | null>(null);

  useEffect(() => {
    void loadCards(0, '');
  }, [loadCards]);

  const handleSearch = useCallback(() => {
    void loadCards(0, search);
  }, [loadCards, search]);

  const handlePageChange = useCallback((offset: number) => {
    void loadCards(offset);
  }, [loadCards]);

  const handleSave = useCallback(async () => {
    if (!editingCard) return;
    try {
      if (isCreating) {
        await createCard(editingCard);
        setMessage('卡牌已建立');
      } else if (editingCard.code) {
        await updateCard(editingCard.code, editingCard);
        setMessage('卡牌已更新');
      }
      setEditingCard(null);
      setIsCreating(false);
    } catch {
      setMessage('操作失敗');
    }
    setTimeout(() => { setMessage(null); }, 2000);
  }, [editingCard, isCreating, createCard, updateCard]);

  const handleDelete = useCallback(async (code: string) => {
    await deleteCard(code);
    setMessage('卡牌已停用');
    setTimeout(() => { setMessage(null); }, 2000);
  }, [deleteCard]);

  const handleImportJSON = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      void file.text().then((text) => {
        const data: unknown = JSON.parse(text);
        const cardsArray = Array.isArray(data) ? data as unknown[] : [];
        return importCards(cardsArray);
      }).then((result) => {
        setMessage(`匯入完成: ${String(result.imported)}/${String(result.total)} 張`);
        setTimeout(() => { setMessage(null); }, 3000);
      }).catch(() => {
        setMessage('匯入失敗');
        setTimeout(() => { setMessage(null); }, 3000);
      });
    };
    input.click();
  }, [importCards]);

  const handleImportSheet = useCallback(async () => {
    if (!sheetId.trim()) return;
    try {
      const result = await importFromSheet(sheetId.trim());
      setMessage(`從 Google Sheet 匯入: ${String(result.imported)}/${String(result.total)} 張`);
      setShowImportSheet(false);
      setSheetId('');
    } catch {
      setMessage('Google Sheet 匯入失敗');
    }
    setTimeout(() => { setMessage(null); }, 3000);
  }, [sheetId, importFromSheet]);

  const handleReparseAll = useCallback(async () => {
    try {
      const result = await AdminAPI.reparseAllCards();
      setMessage(`已重新解析 ${String(result.reparsed)} 張卡牌`);
      void loadCards();
    } catch {
      setMessage('重新解析失敗');
    }
    setTimeout(() => { setMessage(null); }, 3000);
  }, [loadCards]);

  const totalPages = Math.ceil(cardsTotalCount / 50);
  const currentPage = Math.floor(cardsOffset / 50) + 1;

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>🃏 卡牌管理</h1>
        <div style={styles.actions}>
          {message && <span style={styles.message}>{message}</span>}
          <button style={{ ...styles.actionButton, backgroundColor: '#6a5a3a' }} onClick={() => { void handleReparseAll(); }}>
            🔄 全部重新解析
          </button>
          <button style={styles.actionButton} onClick={handleImportJSON}>
            📄 匯入 JSON
          </button>
          <button style={styles.actionButton} onClick={() => { setShowImportSheet(!showImportSheet); }}>
            📊 匯入 Google Sheet
          </button>
          <button style={{ ...styles.actionButton, backgroundColor: '#3a8a3a' }} onClick={() => {
            setIsCreating(true);
            setEditingCard({ code: '', name: '', faction: '', type: 'unit', rarity: 'N' });
          }}>
            ＋ 新增卡牌
          </button>
        </div>
      </div>

      {/* Google Sheet Import */}
      {showImportSheet && (
        <div style={styles.importSheet}>
          <input
            style={styles.input}
            placeholder="Google Sheet ID"
            value={sheetId}
            onChange={(e) => { setSheetId(e.target.value); }}
          />
          <button style={styles.actionButton} onClick={() => { void handleImportSheet(); }}>
            匯入
          </button>
          <button style={styles.cancelButton} onClick={() => { setShowImportSheet(false); }}>
            取消
          </button>
        </div>
      )}

      {/* Search */}
      <div style={styles.searchBar}>
        <input
          style={{ ...styles.input, flex: 1 }}
          placeholder="搜尋卡牌 (代碼、名稱、陣營、卡包)..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
        />
        <button style={styles.actionButton} onClick={handleSearch}>搜尋</button>
        <span style={styles.count}>共 {cardsTotalCount} 張</span>
      </div>

      {error && <div style={styles.error}>❌ {error}</div>}

      {/* Edit Modal */}
      {editingCard && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>{isCreating ? '新增卡牌' : `編輯: ${editingCard.code ?? ''}`}</h3>
            <div style={styles.formGrid}>
              <label style={styles.label}>
                代碼
                <input style={styles.input} value={editingCard.code ?? ''} disabled={!isCreating}
                  onChange={(e) => { setEditingCard({ ...editingCard, code: e.target.value }); }} />
              </label>
              <label style={styles.label}>
                名稱
                <input style={styles.input} value={editingCard.name ?? ''}
                  onChange={(e) => { setEditingCard({ ...editingCard, name: e.target.value }); }} />
              </label>
              <label style={styles.label}>
                陣營
                <input style={styles.input} value={editingCard.faction ?? ''}
                  onChange={(e) => { setEditingCard({ ...editingCard, faction: e.target.value }); }} />
              </label>
              <label style={styles.label}>
                類型
                <select style={styles.input} value={editingCard.type ?? 'unit'}
                  onChange={(e) => { setEditingCard({ ...editingCard, type: e.target.value }); }}>
                  <option value="unit">單位卡</option>
                  <option value="spell">魔法卡</option>
                  <option value="field">場地卡</option>
                </select>
              </label>
              <label style={styles.label}>
                稀有度
                <select style={styles.input} value={editingCard.rarity ?? 'N'}
                  onChange={(e) => { setEditingCard({ ...editingCard, rarity: e.target.value }); }}>
                  <option value="SSR">SSR</option>
                  <option value="SR">SR</option>
                  <option value="R">R</option>
                  <option value="N">N</option>
                  <option value="Promo">Promo</option>
                  <option value="-">-</option>
                </select>
              </label>
              <label style={styles.label}>
                卡包
                <input style={styles.input} value={editingCard.pack ?? ''}
                  onChange={(e) => { setEditingCard({ ...editingCard, pack: e.target.value }); }} />
              </label>
              <label style={{ ...styles.label, gridColumn: '1 / -1' }}>
                效果
                <textarea style={{ ...styles.input, minHeight: '60px' }} value={editingCard.effect ?? ''}
                  onChange={(e) => { setEditingCard({ ...editingCard, effect: e.target.value }); }} />
              </label>
            </div>
            <div style={styles.modalActions}>
              <button style={{ ...styles.actionButton, backgroundColor: '#3a8a3a' }} onClick={() => { void handleSave(); }}>
                儲存
              </button>
              <button style={styles.cancelButton} onClick={() => { setEditingCard(null); setIsCreating(false); }}>
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Card Table */}
      <div style={styles.table}>
        <div style={styles.tableHeader}>
          <span style={{ ...styles.cell, flex: 1.5 }}>代碼</span>
          <span style={{ ...styles.cell, flex: 2 }}>名稱</span>
          <span style={styles.cell}>陣營</span>
          <span style={styles.cell}>類型</span>
          <span style={{ ...styles.cell, flex: 0.5 }}>ATK</span>
          <span style={{ ...styles.cell, flex: 0.5 }}>分數</span>
          <span style={{ ...styles.cell, flex: 0.5 }}>稀有</span>
          <span style={styles.cell}>卡包</span>
          <span style={{ ...styles.cell, flex: 0.7 }}>解析</span>
          <span style={styles.cell}>操作</span>
        </div>
        {isLoading && cards.length === 0 && (
          <div style={styles.emptyRow}>載入中...</div>
        )}
        {cards.map((card) => (
          <div key={card.code} style={{
            ...styles.tableRow,
            opacity: card.is_active ? 1 : 0.5,
          }}>
            <span style={{ ...styles.cell, flex: 1.5, fontFamily: 'monospace', fontSize: '12px' }}>{card.code}</span>
            <span style={{ ...styles.cell, flex: 2 }}>{card.name}</span>
            <span style={styles.cell}>{card.faction_zh || card.faction}</span>
            <span style={styles.cell}>{card.type_zh || card.type}</span>
            <span style={{ ...styles.cell, flex: 0.5 }}>{card.atk ?? '-'}</span>
            <span style={{ ...styles.cell, flex: 0.5 }}>{card.score ?? '-'}</span>
            <span style={{ ...styles.cell, flex: 0.5, color: rarityColor(card.rarity) }}>{card.rarity}</span>
            <span style={{ ...styles.cell, fontSize: '11px' }}>{card.pack || '-'}</span>
            <span style={{ ...styles.cell, flex: 0.7, fontSize: '11px' }}>
              {card.parse_status === 'verified' ? '✅' : card.parse_status === 'manual' ? '✋' : '🤖'}
            </span>
            <span style={{ ...styles.cell, display: 'flex', gap: '4px' }}>
              <button style={styles.smallButton} onClick={() => { setEditingCard(card); setIsCreating(false); }}>✏️</button>
              <button style={styles.smallButton} onClick={() => { setEffectEditorCard(card); }} title="效果編輯">⚡</button>
              <button style={{ ...styles.smallButton, color: '#ff4444' }} onClick={() => { void handleDelete(card.code); }}>🗑️</button>
            </span>
          </div>
        ))}
        {cards.length === 0 && !isLoading && (
          <div style={styles.emptyRow}>沒有找到卡牌</div>
        )}
      </div>

      {/* Effect Editor Modal */}
      {effectEditorCard && (
        <CardEffectEditor
          card={effectEditorCard}
          onClose={() => { setEffectEditorCard(null); }}
          onUpdated={() => { void loadCards(); setEffectEditorCard(null); }}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            style={styles.pageButton}
            disabled={currentPage <= 1}
            onClick={() => { handlePageChange(cardsOffset - 50); }}
          >
            ← 上一頁
          </button>
          <span style={styles.pageInfo}>{currentPage} / {totalPages}</span>
          <button
            style={styles.pageButton}
            disabled={currentPage >= totalPages}
            onClick={() => { handlePageChange(cardsOffset + 50); }}
          >
            下一頁 →
          </button>
        </div>
      )}
    </div>
  );
}

function rarityColor(rarity: string): string {
  switch (rarity) {
    case 'SSR': return '#ff8800';
    case 'SR': return '#cc44ff';
    case 'R': return '#4488ff';
    case 'N': return '#888888';
    case 'Promo': return '#ffcc00';
    default: return '#666666';
  }
}

const styles: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  title: { fontSize: '24px', margin: 0 },
  actions: { display: 'flex', gap: '8px', alignItems: 'center' },
  actionButton: {
    padding: '8px 14px', fontSize: '13px', backgroundColor: '#3a5a8a', color: '#ffffff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
  },
  cancelButton: {
    padding: '8px 14px', fontSize: '13px', backgroundColor: '#5a3a3a', color: '#ffffff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
  },
  message: { color: '#44ff44', fontSize: '13px' },
  error: { color: '#ff4444', padding: '8px', marginBottom: '8px' },
  searchBar: { display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' },
  input: {
    padding: '8px 12px', fontSize: '14px', backgroundColor: '#2a2a4a', color: '#ffffff',
    border: '1px solid #3a3a5a', borderRadius: '4px', outline: 'none',
  },
  count: { fontSize: '13px', color: '#888888' },
  importSheet: { display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' },
  table: { backgroundColor: '#252545', borderRadius: '8px', overflow: 'hidden' },
  tableHeader: {
    display: 'flex', padding: '10px 12px', backgroundColor: '#1e1e3e',
    fontSize: '12px', fontWeight: 'bold', color: '#888888',
  },
  tableRow: {
    display: 'flex', padding: '8px 12px', borderTop: '1px solid #3a3a5a',
    fontSize: '13px', alignItems: 'center',
  },
  cell: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '8px' },
  emptyRow: { padding: '20px', textAlign: 'center', color: '#666666' },
  smallButton: {
    padding: '4px 6px', fontSize: '12px', backgroundColor: 'transparent',
    border: 'none', cursor: 'pointer', borderRadius: '4px',
  },
  pagination: { display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '16px', alignItems: 'center' },
  pageButton: {
    padding: '8px 16px', fontSize: '13px', backgroundColor: '#3a5a8a', color: '#ffffff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
  },
  pageInfo: { fontSize: '14px', color: '#888888' },
  modal: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#252545', borderRadius: '12px', padding: '24px',
    width: '600px', maxHeight: '80vh', overflow: 'auto',
  },
  modalTitle: { fontSize: '18px', margin: '0 0 16px 0' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' },
  label: { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: '#cccccc' },
  modalActions: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
};
